import { useState, useCallback } from 'react';
import { useTicketHoldMutation } from '../../../../../services/ticketEasyService/ticketEasy-api';
import { useMyProfileQuery } from '../../../../../services/profileService/profile-api';
import { TicketHoldCommand } from '../../../../../services/ticketEasyService/commands/approval/TicketHoldCommand';

interface UseTicketHoldProps {
  ticketId?: string;
  onSuccess?: () => void;
}

export function useTicketHold({ ticketId, onSuccess }: UseTicketHoldProps) {
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holding, setHolding] = useState(false);
  const [holdSuccess, setHoldSuccess] = useState(false);
  const [holdError, setHoldError] = useState<string | null>(null);
  
  const { data: myProfile } = useMyProfileQuery();
  const [holdTicket] = useTicketHoldMutation();
  
  const openHoldDialog = useCallback(() => {
    setHoldDialogOpen(true);
  }, []);
  
  const closeHoldDialog = useCallback(() => {
    setHoldDialogOpen(false);
  }, []);
  
  const handleHoldSubmit = useCallback(async (comments: string) => {
    if (!ticketId) {
      setHoldError("Cannot hold ticket: Missing ticket ID");
      return;
    }
    
    if (!myProfile) {
      setHoldError("Cannot hold ticket: Missing user data");
      return;
    }
    
    setHolding(true);
    setHoldError(null);
    
    try {
      const holdCommand = new TicketHoldCommand({
        ticketId,
        holder: myProfile.identity,
        holderEmail: myProfile.email,
        comments
      });
      
      await holdTicket(holdCommand).unwrap();
      setHoldSuccess(true);
      closeHoldDialog();
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Auto-hide success message after 6 seconds
      setTimeout(() => {
        setHoldSuccess(false);
      }, 6000);
    } catch (error) {
      console.error("Failed to place ticket on hold:", error);
      setHoldError("Failed to place ticket on hold. Please try again.");
    } finally {
      setHolding(false);
    }
  }, [ticketId, myProfile, holdTicket, closeHoldDialog, onSuccess]);
  
  const clearSuccessMessage = useCallback(() => {
    setHoldSuccess(false);
  }, []);
  
  const clearErrorMessage = useCallback(() => {
    setHoldError(null);
  }, []);
  
  return {
    holdDialogOpen,
    holding,
    holdSuccess,
    holdError,
    openHoldDialog,
    closeHoldDialog,
    handleHoldSubmit,
    clearSuccessMessage,
    clearErrorMessage
  };
}
